import { ShopActivity } from "./ShopLoading";
import { useState } from "react";
import type { ShopOption } from "@stackje/site-builder";
import { Plus } from "lucide-react";
import { newShopId, variantName } from "../../lib/shop";
import { currencySymbol, parseMoney } from "../../lib/shopMoney";
import { Card, CheckRow, Field, FieldGroup } from "../area";
import { PriceFields, StockFields } from "./variantFields";
import type { ProductEditorState } from "./useProductEditor";

/** One option (Size, Glaze): its name, its values side by side, and what can be done to it. */
function OptionRow({ editor, option, index }: { editor: ProductEditorState; option: ShopOption; index: number }) {
  const { options, setOptions, product, busy, draft } = editor;
  const locked = busy || Boolean(draft);
  const current = () => options ?? product.details.options;
  const change = (next: ShopOption) => setOptions(current().map((item, at) => at === index ? next : item));
  return <div className="bw-shop-option">
    <Field label="Option name">
      <input aria-label={`Option ${index + 1} name`} disabled={locked} value={option.name} onChange={(event) => change({ ...option, name: event.target.value })} />
    </Field>
    <div className="bw-shop-values" role="group" aria-label={`Values of ${option.name}`}>
      <span className="bw-shop-values-label">Values</span>
      <div>
        {option.values.map((value, at) => <input key={value.id} aria-label={`Value ${at + 1}, option ${index + 1}`} disabled={locked} value={value.label} onChange={(event) => change({ ...option, values: option.values.map((entry, j) => j === at ? { ...entry, label: event.target.value } : entry) })} />)}
        <button type="button" className="bw-btn bw-btn--text bw-area-link" disabled={locked} onClick={() => change({ ...option, values: [...option.values, { id: newShopId("value"), label: "New value" }] })}><Plus size={14} aria-hidden="true" /> Add a value</button>
      </div>
    </div>
    <button type="button" className="bw-btn bw-btn--text bw-area-link" data-tone="danger" disabled={locked} onClick={() => setOptions(current().filter((_, at) => at !== index))}>Remove option</button>
  </div>;
}

function OptionsCard({ editor }: { editor: ProductEditorState }) {
  const { options, setOptions, product, busy, draft } = editor;
  const shown = options ?? product.details.options;
  const locked = busy || Boolean(draft);
  return <Card title="Options" hint="up to 3" actions={<button type="button" className="bw-btn bw-btn--text bw-area-link" disabled={shown.length >= 3 || locked} onClick={() => setOptions([...shown, { id: newShopId("option"), name: "New option", values: [{ id: newShopId("value"), label: "New value" }] }])}><Plus size={14} aria-hidden="true" /> Add option</button>}>
    {shown.length === 0 && <p>No options yet. Add one, like Size or Glaze, to sell it in different versions.</p>}
    {shown.map((option, index) => <OptionRow key={option.id} editor={editor} option={option} index={index} />)}
    {draft && <p>Save or discard your price changes before changing the options.</p>}
  </Card>;
}

/** Set one price, or one stock count, on every variant at once. Prices wait for Save; stock is live. */
function SetForAll({ editor }: { editor: ProductEditorState }) {
  const { api, siteId, shop, product, variants, currency, language, busy, options, work, setError, setDraft } = editor;
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("");
  const [group, setGroup] = useState("");
  const groups = product.details.options.flatMap((option) => option.values.map((value) => ({ key: `${option.id}:${value.id}`, optionId: option.id, valueId: value.id, label: value.label })));
  const selected = groups.find((item) => item.key === group);
  const setPriceForAll = () => {
    if (parseMoney(price, currency, language) === null) { setError("Check the price for all variants."); return; }
    setDraft(variants.map((item) => !selected || item.values[selected.optionId] === selected.valueId ? { ...item, priceText: price } : item));
  };
  const setStockForAll = () => {
    const quantity = Number(stock);
    if (!/^\d+$/.test(stock) || !Number.isSafeInteger(quantity)) { setError("Stock for all variants must be a whole number."); return; }
    void work(async () => {
      for (const variant of product.variants) {
        const row = shop.stock.find((item) => item.variantId === variant.id)!;
        await api.saveVariantStock(siteId, variant.id, row.rowVersion, { tracked: true, quantity });
      }
    }, false);
  };
  return <div className="bw-shop-bulk">
    <div>
      {groups.length > 0 && <Field label="Set prices for"><select aria-label="Set prices for" disabled={busy || Boolean(options)} value={group} onChange={(event) => setGroup(event.target.value)}><option value="">All variants</option>{groups.map((item) => <option key={item.key} value={item.key}>All {item.label}</option>)}</select></Field>}
      <Field label={selected ? `Price for all ${selected.label}` : "Price for all"}><span className="bw-area-prefixed"><span>{currencySymbol(currency, language)}</span><input aria-label="Price for all variants" disabled={busy} inputMode="decimal" value={price} onChange={(event) => setPrice(event.target.value)} /></span></Field>
      <button type="button" className="bw-btn bw-btn--quiet" disabled={busy || Boolean(options)} onClick={setPriceForAll}>{selected ? `Set price for all ${selected.label}` : "Set price for all"}</button>
    </div>
    <div>
      <Field label="Stock for all"><input aria-label="Stock for all variants" disabled={busy} inputMode="numeric" value={stock} onChange={(event) => setStock(event.target.value)} /></Field>
      <button type="button" className="bw-btn bw-btn--quiet" disabled={busy} onClick={setStockForAll}>{busy ? <ShopActivity announce={false} label="Setting stock…" /> : "Set stock for all"}</button>
    </div>
    <small className="bw-area-hint">Prices wait for Save. Stock is live as soon as you set it.</small>
  </div>;
}

function VariantRow({ editor, index }: { editor: ProductEditorState; index: number }) {
  const { product, busy, options, variants, updateVariant } = editor;
  const variant = variants[index]!;
  const locked = busy || Boolean(options);
  return <div className="bw-shop-variant">
    <strong>{variantName(product, variant.values)}</strong>
    <FieldGroup columns={3} stack>
      <PriceFields editor={editor} variant={variant} index={index} />
      <Field label="SKU"><input disabled={locked} value={variant.sku} onChange={(event) => updateVariant(index, { sku: event.target.value })} /></Field>
    </FieldGroup>
    <FieldGroup columns={3} stack>
      <Field label="Photo">
        <select disabled={locked} value={variant.photo?.assetId ?? ""} onChange={(event) => updateVariant(index, { photo: product.details.photos.find((photo) => photo.assetId === event.target.value) ?? null })}>
          <option value="">Product photo</option>
          {product.details.photos.map((photo, at) => <option key={photo.assetId} value={photo.assetId}>Photo {at + 1}</option>)}
        </select>
      </Field>
      <StockFields editor={editor} variant={variant} />
      <CheckRow label="Selling" disabled={locked} checked={variant.available} onChange={(event) => updateVariant(index, { available: event.target.checked })} />
    </FieldGroup>
  </div>;
}

export function VariantsTab({ editor }: { editor: ProductEditorState }) {
  const { variants } = editor;
  return <>
    <OptionsCard editor={editor} />
    <Card title="Variants" hint={String(variants.length)}>
      <SetForAll editor={editor} />
      {variants.map((variant, index) => <VariantRow key={variant.id} editor={editor} index={index} />)}
      <p>Stock counts down as orders come in and isn't part of product history. Turn off Selling to stop sales of one variant without deleting it.</p>
    </Card>
  </>;
}
